import { useQueryClient } from '@tanstack/react-query'
import { Illustrations, Sheet, Typography } from '@cannect/new-components/atoms'
import { AnimatePresence, motion } from 'framer-motion'
import {
  KEY_SUPPORT_CONVERSATION,
  useConversationStatus,
  useMarkConversationRead,
  useOpenConversation,
  useCancellationReasons,
  useCloseConversation,
  useSendMessage,
  useUploadAttachment
} from '@cannect/services/resources/supportChat'
import {
  TOpenConversationResponse,
  TSupportAttachment,
  TSupportConversationStatus
} from '@cannect/services/resources/supportChat/types'
import { UserOrder } from '@cannect/pages/UserOrderList/types'
import { Fragment, ReactNode, useEffect, useRef, useState } from 'react'
import { Sparkles } from 'lucide-react'
import {
  FiAlertCircle,
  FiList,
  FiChevronRight,
  FiClock,
  FiCreditCard,
  FiFileText,
  FiHeadphones,
  FiHelpCircle,
  FiLogOut,
  FiPackage,
  FiRefreshCw,
  FiTruck,
  FiWifiOff,
  FiXCircle
} from 'react-icons/fi'
import { dayLabel, startsNewBlock, startsNewDay } from './grouping'
import MessageBubble, { CHAT_SURFACE, DaySeparator } from './MessageBubble'
import MessageComposer from './MessageComposer'
import SatisfactionSurvey from './SatisfactionSurvey'
import useConversationMessages from './useConversationMessages'

type TSubMenu = 'cancelar' | 'troca'

const EXCHANGE_REASONS: { label: string; message: string }[] = [
  {
    label: 'Recebi ou comprei o produto errado',
    message: 'Quero trocar: recebi ou comprei o produto errado.'
  },
  { label: 'O produto chegou avariado', message: 'O produto do meu pedido chegou avariado.' },
  {
    label: 'Produto vencido ou perto do vencimento',
    message: 'O produto do meu pedido chegou vencido ou perto do vencimento.'
  },
  { label: 'Desisti e quero devolver', message: 'Desisti da compra e quero devolver o produto.' },
  {
    label: 'Outro motivo',
    message: 'Quero fazer uma troca ou devolução por outro motivo.'
  }
]

const QUICK_REPLIES: { icon: ReactNode; label: string; message: string; subMenu?: TSubMenu }[] = [
  { icon: <FiTruck />, label: 'Onde está meu pedido?', message: 'Onde está meu pedido?' },
  { icon: <FiClock />, label: 'Prazo de entrega', message: 'Qual o prazo de entrega do meu pedido?' },
  { icon: <FiCreditCard />, label: 'Dúvida sobre pagamento', message: 'Tenho uma dúvida sobre o pagamento' },
  { icon: <FiFileText />, label: 'Nota fiscal ou Invoice', message: 'Preciso da nota fiscal/Invoice do meu pedido' },
  {
    icon: <FiRefreshCw />,
    label: 'Troca ou devolução',
    message: 'Quero fazer uma troca ou devolução',
    subMenu: 'troca'
  },
  { icon: <FiHelpCircle />, label: 'Outro assunto', message: 'Preciso de ajuda com outro assunto' }
]

const formatWait = (seconds?: number | null) => {
  if (seconds === null || seconds === undefined) return null
  if (seconds < 60) return 'menos de um minuto'

  const minutes = Math.round(seconds / 60)

  if (minutes === 1) return 'cerca de 1 minuto'
  // Ninguém lê "cerca de 90 minutos" e entende uma hora e meia.
  if (minutes < 60) return `cerca de ${minutes} minutos`

  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const horas = hours === 1 ? '1 hora' : `${hours} horas`

  return rest === 0 ? `cerca de ${horas}` : `cerca de ${horas} e ${rest} min`
}

const STATUS_TONE: Record<TSupportConversationStatus, { label: string; dot: string }> = {
  bot: { label: 'Bia', dot: 'bg-primary-600' },
  queued: { label: 'Aguardando atendente', dot: 'animate-pulse bg-tertiary-500' },
  assigned: { label: 'Falando com especialista', dot: 'animate-pulse bg-green-500' },
  closed: { label: 'Atendimento encerrado', dot: 'bg-neutral-300' }
}

const initials = (name?: string | null) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

/** Quem está do outro lado agora: a Bia, a fila ou o atendente. */
const HeaderAvatar = ({ status, agentName }: { status: TSupportConversationStatus; agentName?: string | null }) => {
  if (status === 'assigned') {
    return (
      <span
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-600 to-primary-800 text-sm font-semibold text-white shadow-sm">
        {initials(agentName) || <FiHeadphones size={16} />}
      </span>
    )
  }

  if (status === 'queued') {
    return (
      <span
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tertiary-100 text-tertiary-900 shadow-sm">
        <FiClock size={17} />
      </span>
    )
  }

  return (
    <span
      aria-hidden
      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm ${
        status === 'closed' ? 'opacity-50 grayscale' : ''
      }`}>
      <Sparkles size={18} />
    </span>
  )
}

type OrderHelpPanelProps = {
  /** Pedido sobre o qual o paciente pediu ajuda. `null` mantém o painel fechado. */
  orderId: number | null
  onClose: () => void
  /** Até três pedidos pagos e não perdidos para oferecer no fim do assunto. */
  outrosPedidos?: UserOrder[]
  /** Troca o pedido em atendimento sem encerrar a conversa atual. */
  onTrocarPedido?: (id: number) => void
}

const OrderHelpPanel = ({ orderId, onClose, outrosPedidos = [], onTrocarPedido }: OrderHelpPanelProps) => {
  const [isSending, setIsSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  /** Menu de assuntos: fecha ao escolher um, e o paciente pode reabrir. */
  const [isMenuOpen, setIsMenuOpen] = useState(true)
  /** Segundo passo do cancelamento: a lista de motivos. */
  const [subMenu, setSubMenu] = useState<TSubMenu | null>(null)
  const [isConfirmingClose, setIsConfirmingClose] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const posicionado = useRef<string | null>(null)
  const queryClient = useQueryClient()

  // A conversa só é criada quando o painel abre com um pedido — abrir a lista
  // de pedidos não gera atendimento nenhum.
  const { data: opened, isLoading, error: openError } = useOpenConversation(orderId)

  const uuid = opened?.conversation.uuid ?? null
  const firebaseToken = opened?.firebase_token

  const {
    messages,
    meta,
    isReady,
    error: chatError
  } = useConversationMessages({
    conversationUuid: uuid,
    firebaseToken
  })

  // O `meta` do Realtime Database muda na hora em que o atendente assume; o
  // polling do MySQL é o que traz posição na fila e estimativa.
  //
  // O último status visto fica guardado: se o nó do RTDB sumir, cair no status
  // da abertura faria a tela voltar para 'bot' — uma conversa encerrada
  // voltaria a parecer ativa, aceitando mensagem. Manter o último conhecido
  // erra para o lado seguro.
  const lastMetaStatus = useRef<TSupportConversationStatus | null>(null)

  if (meta?.status) lastMetaStatus.current = meta.status as TSupportConversationStatus

  const currentStatus: TSupportConversationStatus =
    (meta?.status as TSupportConversationStatus) ?? lastMetaStatus.current ?? opened?.conversation.status ?? 'bot'

  const { data: status } = useConversationStatus(uuid, currentStatus === 'queued')

  const conversation = status ?? opened?.conversation ?? null
  const queue = conversation?.queue ?? null

  const sendMessage = useSendMessage()
  const uploadAttachment = useUploadAttachment()
  const closeConversation = useCloseConversation()
  const cancellationReasons = useCancellationReasons()

  /**
   * O botão de WhatsApp do Botmaker é injetado no body por um script do
   * `index.html`, com z-index acima de todo o React — não dá para escondê-lo
   * por componente. A classe aqui aciona a regra que vive lá.
   */
  useEffect(() => {
    if (!orderId) return undefined

    document.body.classList.add('support-chat-open')

    return () => document.body.classList.remove('support-chat-open')
  }, [orderId])

  // Cada pedido começa com o menu aberto.
  useEffect(() => {
    setIsMenuOpen(true)
    setIsConfirmingClose(false)
  }, [orderId])

  /**
   * Conversa encerrada sai do cache ao reabrir o painel.
   *
   * A query de abertura é um POST que cria a conversa, então ela não pode
   * refetchar sozinha — mas, congelada, devolvia para sempre o atendimento
   * encerrado e o paciente não conseguia pedir ajuda de novo no mesmo pedido.
   * Limpar só na reabertura resolve os dois: nada é criado sem clique, e um
   * clique novo começa um atendimento novo.
   */
  useEffect(() => {
    if (!orderId) return

    const cached = queryClient.getQueryData<TOpenConversationResponse>([KEY_SUPPORT_CONVERSATION, orderId])

    if (cached?.conversation?.status === 'closed') {
      queryClient.removeQueries({ queryKey: [KEY_SUPPORT_CONVERSATION, orderId] })
    }
  }, [orderId, queryClient])


  useEffect(() => {
    if (!messages.length) return

    if (posicionado.current !== uuid) {
      posicionado.current = uuid
      bottomRef.current?.scrollIntoView({ block: 'end' })

      return
    }

    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, currentStatus, uuid])

  // Com a conversa na tela, o que chega já foi lido: zera as não lidas (uma
  // chamada só para várias mensagens seguidas).
  const { mutate: markRead } = useMarkConversationRead()

  useEffect(() => {
    if (!uuid || messages.length === 0) return undefined

    const timer = setTimeout(() => markRead(uuid), 1500)

    return () => clearTimeout(timer)
  }, [uuid, messages.length, markRead])

  const hasPatientMessage = messages.some((message) => message.sender_type === 'patient')

  /*
    Os outros pedidos só aparecem quando a Bia encerra o assunto — oferecer
    troca no meio da conversa convida o paciente a largar o problema pela
    metade.

    O sinal é um campo, não o texto: a Bia marca `topic_finished` na resposta
    de fechamento, o backend grava no `meta` e cada resposta nova regrava. Antes
    o portal procurava "Finalizamos seu atendimento" na mensagem, e um "De
    nada!" escrito pelo SDR escondia as opções.
  */
  const assuntoFinalizado = meta?.topic_finished === true
  const showQuickReplies = currentStatus === 'bot' && isMenuOpen
  const isClosed = currentStatus === 'closed'
  // Encerrar só existe depois de alguma conversa: sem mensagem não há o que
  // arquivar, e o botão no painel vazio só convida a sair.
  const canClose = !isClosed && !openError && hasPatientMessage

  /**
   * Escrever NÃO depende do Firebase.
   *
   * A mensagem sai por `POST /conversations/:uuid/messages` e é o backend que
   * grava no Realtime Database. O Firebase no navegador serve só para ESCUTAR.
   * Travar o campo quando a escuta falha deixaria o paciente mudo por causa de
   * um problema que não impede o envio.
   */
  const canWrite = !!uuid && !isClosed

  const handleSend = async (content: string, files: File[]) => {
    if (!uuid || (!content && files.length === 0)) return

    setIsSending(true)
    setSendError(null)
    setIsMenuOpen(false)

    try {
      // Os anexos sobem primeiro: a mensagem carrega as URLs, então ela só pode
      // ser publicada quando todos os arquivos já estiverem no S3.
      const attachments: TSupportAttachment[] = await Promise.all(
        files.map((file) => uploadAttachment.mutateAsync({ uuid, file }))
      )

      await sendMessage.mutateAsync({
        uuid,
        content,
        attachments
      })
    } catch (requestError: any) {
      setSendError(requestError?.response?.data?.message ?? 'Não conseguimos enviar sua mensagem. Tente de novo.')
    } finally {
      setIsSending(false)
    }
  }

  const renderStatusBanner = () => {
    if (currentStatus === 'assigned') {
      return (
        <div className="flex items-center gap-3 border-b border-primary-100 bg-primary-50 px-4 py-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-primary-800 shadow-sm">
            <FiHeadphones size={14} />
          </span>
          <span className="text-xs text-primary-900">
            {meta?.agent_name ? (
              <>
                <strong className="font-semibold">{meta.agent_name}</strong> está cuidando do seu atendimento.
              </>
            ) : (
              'Um especialista assumiu seu atendimento.'
            )}
          </span>
        </div>
      )
    }

    if (currentStatus !== 'queued') return null

    if (queue && !queue.within_business_hours) {
      return (
        <div className="flex items-start gap-3 border-b border-tertiary-200 bg-tertiary-100 px-4 py-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-tertiary-900 shadow-sm">
            <FiClock size={14} />
          </span>
          <span className="text-xs text-tertiary-900">
            <strong className="block font-semibold">Fora do horário de atendimento</strong>
            Atendemos de segunda a sexta, das 9h às 18h. Você já está na fila e será chamado assim que abrirmos.
          </span>
        </div>
      )
    }

    const wait = formatWait(queue?.estimated_wait_seconds)

    return (
      <div className="flex items-center gap-3 border-b border-tertiary-200 bg-tertiary-100 px-4 py-2.5">
        {/* A posição é o número que acalma: fica grande, no lugar do ícone. */}
        <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-xl bg-white text-tertiary-900 shadow-sm">
          {queue?.position ? (
            <>
              <span className="text-base font-bold leading-none">{queue.position}º</span>
              <span className="text-[8px] uppercase tracking-wide">na fila</span>
            </>
          ) : (
            <FiClock className="animate-pulse" size={16} />
          )}
        </span>
        <span className="text-xs text-tertiary-900">
          <strong className="block font-semibold">Você está na fila{wait ? ` · espera ${wait}` : ''}</strong>
          Pode continuar escrevendo — o especialista lê tudo ao entrar.
        </span>
      </div>
    )
  }

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex flex-1 flex-col gap-3 pt-2" aria-label="Abrindo seu atendimento" aria-busy="true">
          <div className="flex items-start gap-2">
            <span className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-neutral-200" />
            <span className="h-16 w-3/4 animate-pulse rounded-2xl rounded-tl-md bg-white shadow-sm" />
          </div>
          {[0, 1, 2].map((item) => (
            <span key={item} className="ml-10 h-11 w-2/3 animate-pulse rounded-xl bg-white shadow-sm" />
          ))}
          <p className="mt-2 text-center text-xs text-neutral-500">Abrindo seu atendimento…</p>
        </div>
      )
    }

    // Só a falha de ABRIR a conversa justifica esvaziar a tela: sem conversa não
    // há para onde enviar nada.
    if (openError) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
          <span className="relative flex h-20 w-20 items-center justify-center">
            <span className="absolute inset-0 rounded-full bg-red-100/70" />
            <span className="absolute inset-3 rounded-full bg-red-50" />
            <FiAlertCircle size={28} className="relative text-red-500" />
          </span>
          <p className="m-0 text-sm font-semibold text-neutral-800">Não conseguimos abrir o chat agora.</p>
          <p className="m-0 text-xs text-neutral-500">Pode ser uma instabilidade rápida. Tente de novo em instantes.</p>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: [KEY_SUPPORT_CONVERSATION, orderId] })}
            className="mt-1 flex h-10 items-center gap-2 rounded-xl border-0 bg-primary-800 px-4 text-sm font-semibold text-white transition hover:bg-primary-900">
            <FiRefreshCw size={14} />
            Tentar de novo
          </button>
        </div>
      )
    }

    return (
      <>
        <div className="flex gap-2">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm">
            <Sparkles size={15} />
          </span>
          <div className="rounded-2xl rounded-tl-md bg-white px-3.5 py-2.5 shadow-sm ring-1 ring-violet-200">
            <p className="text-sm leading-relaxed text-neutral-800">
              Oi! Sou a Bia, assistente da Cannect. Estou aqui para ajudar com o{' '}
              <strong className="font-semibold">pedido #{orderId}</strong>.
            </p>
          </div>
        </div>

        {messages.map((message, index) => {
          const novoDia = startsNewDay(message, messages[index - 1])
          const dia = novoDia ? dayLabel(message.created_at) : null

          return (
            <Fragment key={message.id}>
              {dia && <DaySeparator label={dia} />}
              <MessageBubble
                message={message}
                isOwn={message.sender_type === 'patient'}
                showSender={novoDia || startsNewBlock(message, messages[index - 1])}
              />
            </Fragment>
          )
        })}

        {showQuickReplies && subMenu && (
          <div className="flex flex-col gap-2 pt-1">
            <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              {subMenu === 'cancelar' ? 'Por que você quer cancelar?' : 'O que aconteceu com o produto?'}
            </span>

            {(subMenu === 'cancelar'
              ? (cancellationReasons.data ?? []).map((reason) => ({
                  label: reason,
                  message: `Quero cancelar o pedido. Motivo: ${reason}`
                }))
              : EXCHANGE_REASONS
            ).map((option) => (
              <button
                key={option.label}
                type="button"
                disabled={!canWrite || isSending}
                onClick={() => {
                  setSubMenu(null)
                  handleSend(option.message, [])
                }}
                className="rounded-xl border border-solid border-neutral-200 bg-white px-3 py-2.5 text-left text-sm text-neutral-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 disabled:opacity-50">
                {option.label}
              </button>
            ))}

            <button
              type="button"
              onClick={() => setSubMenu(null)}
              className="border-0 bg-transparent px-1 py-1 text-left text-xs text-neutral-500 underline">
              Voltar aos assuntos
            </button>
          </div>
        )}

        {showQuickReplies && !subMenu && (
          <div className="flex flex-col gap-2 pt-1">
            <span className="px-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
              Sobre o que você quer falar?
            </span>

            <button
              type="button"
              disabled={!canWrite || isSending}
              onClick={() => setSubMenu('cancelar')}
              className="flex items-center gap-3 rounded-xl border border-solid border-neutral-200 bg-white px-3 py-2.5 text-left text-sm text-neutral-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 disabled:opacity-50">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-800">
                <FiXCircle />
              </span>
              <span className="flex-1 font-medium">Cancelar meu pedido</span>
              <FiChevronRight className="shrink-0 text-neutral-400" />
            </button>

            {QUICK_REPLIES.map((option) => (
              <button
                key={option.label}
                type="button"
                disabled={!canWrite || isSending}
                onClick={() => (option.subMenu ? setSubMenu(option.subMenu) : handleSend(option.message, []))}
                className="flex items-center gap-3 rounded-xl border border-solid border-neutral-200 bg-white px-3 py-2.5 text-left text-sm text-neutral-800 shadow-sm transition hover:border-primary-300 hover:bg-primary-50 disabled:opacity-50">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-800">
                  {option.icon}
                </span>
                <span className="flex-1 font-medium">{option.label}</span>
                <FiChevronRight className="shrink-0 text-neutral-400" />
              </button>
            ))}
          </div>
        )}

        {isClosed && (
          <div className="flex flex-col gap-3 pt-2">
            {/* A ilustração de "jornada concluída" do design system, a mesma da procuração. */}
            <div className="flex flex-col items-center gap-1 rounded-2xl bg-white px-4 pb-4 pt-5 text-center shadow-sm ring-1 ring-black/5">
              <Illustrations.JourneyFinished width={84} height={90} aria-hidden />
              <strong className="mt-1 text-sm font-semibold text-neutral-800">Atendimento encerrado</strong>
              <span className="text-xs text-neutral-500">Se precisar de novo, é só pedir ajuda pelo pedido.</span>
            </div>

            {/*
              A pesquisa só aparece para quem foi atendido de fato. Perguntar
              "como foi o atendimento" a quem abriu e fechou sem conversar
              contamina a média com respostas sobre nada.
            */}
            {uuid && hasPatientMessage && (
              <SatisfactionSurvey conversationUuid={uuid} answeredScore={conversation?.nps_score ?? null} />
            )}
          </div>
        )}

        <div ref={bottomRef} />
      </>
    )
  }

  // Enquanto a escuta não subiu, o cabeçalho diz isso em vez de anunciar um
  // estado que ainda não está sendo acompanhado em tempo real.
  const tone =
    uuid && !isReady && !chatError
      ? { label: 'Conectando…', dot: 'animate-pulse bg-neutral-300' }
      : STATUS_TONE[currentStatus]

  return (
    <Sheet.Root open={!!orderId} onOpenChange={(open) => !open && onClose()}>
      {/*
        Overlay próprio, mais leve que o padrão (`bg-black/80`): o chat abre por
        cima da lista de pedidos e o paciente continua consultando o pedido ali
        atrás enquanto escreve. Escurecer tudo transforma um painel de apoio em
        uma janela modal que esconde justamente o que se está discutindo.
      */}
      <Sheet.Portal>
        <Sheet.Overlay className="bg-neutral-900/25 backdrop-blur-[2px]" />
      </Sheet.Portal>

      <Sheet.Content
        hideOverlay
        side="right"
        aria-label="Chat de ajuda do pedido"
        className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 rounded-none bg-white p-0 shadow-2xl sm:w-[440px] sm:max-w-[92vw] sm:rounded-l-2xl md:max-w-[440px] lg:max-w-[460px]">
        {/*
          `pl-12` em todas as larguras: o botão de fechar do Sheet é ancorado em
          `left-2` neste lado e, sem essa folga, ele cai em cima do avatar — que
          um dia vira a foto da Bia e ficaria ilegível do mesmo jeito.
        */}
        <Sheet.Header className="mt-0 flex-row items-center gap-3 border-b border-neutral-200 bg-white py-3 pl-12 pr-3">
          <HeaderAvatar status={currentStatus} agentName={meta?.agent_name} />

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <Sheet.Title className="truncate text-base leading-tight">
              {currentStatus === 'assigned' && meta?.agent_name ? meta.agent_name : 'Ajuda do seu pedido'}
            </Sheet.Title>
            <Sheet.Description className="flex items-center gap-1.5 text-xs">
              <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${tone.dot}`} />
              <span className="truncate">
                {tone.label} · Pedido #{orderId}
              </span>
            </Sheet.Description>
          </div>

          {canClose && (
            <button
              type="button"
              onClick={() => setIsConfirmingClose(true)}
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-solid border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 transition hover:border-red-200 hover:bg-red-50 hover:text-red-700">
              <FiLogOut size={13} />
              Encerrar
            </button>
          )}

          {/* Lista de assuntos: some quando o menu já é o que está na tela.
              Ícone de lista, não seta: a ação não é voltar na navegação, é
              reabrir o menu de assuntos — e seta de voltar à direita, com o X
              à esquerda, inverte a convenção e confunde. */}
          {currentStatus === 'bot' && !isMenuOpen && !openError && (
            <button
              type="button"
              onClick={() => setIsMenuOpen(true)}
              title="Ver os assuntos"
              aria-label="Ver os assuntos"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-solid border-neutral-200 bg-white text-neutral-600 transition hover:bg-neutral-50">
              <FiList size={16} />
            </button>
          )}
        </Sheet.Header>

        {renderStatusBanner()}

        {/* Falha de escuta não trava a conversa: avisa e deixa escrever. */}
        {chatError && !openError && (
          <div className="flex items-start gap-2 bg-amber-50 px-4 py-2 text-[11px] text-amber-800">
            <FiWifiOff className="mt-0.5 shrink-0" />
            <span>Pode escrever normalmente. Estamos reconectando para mostrar as respostas aqui.</span>
          </div>
        )}

        <div className={`flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-4 ${CHAT_SURFACE}`}>{renderBody()}</div>

        {sendError && <p className="bg-red-50 px-4 py-2 text-center text-xs text-red-700">{sendError}</p>}

        {/*
          Não há atalho para o humano: quem decide transferir é a assistente,
          pelo handoff. O paciente pedir a fila por conta própria enchia a fila
          de casos que ela resolvia sozinha.
        */}

        {/*
          Outros pedidos do paciente, para quando a assistente perguntar se ele
          quer falar de outro ou encerrar.

          Trocar de pedido NÃO encerra esta conversa: ela fica em `bot`, sem
          NPS, e ele pode voltar. A pesquisa pertence ao encerramento de fato,
          logo abaixo. Só aparece enquanto a conversa está com a assistente —
          na fila ou com atendente, mudar de assunto no meio confundiria quem
          está atendendo.
        */}
        {currentStatus === 'bot' && !openError && assuntoFinalizado && outrosPedidos.length > 0 && onTrocarPedido && (
          <div className="border-t border-neutral-200 bg-white px-4 py-2.5">
            <Typography.Text type="captionOne" muted className="mb-1.5 block">
              Quer falar sobre outro pedido?
            </Typography.Text>
            <div className="flex flex-col gap-1.5">
              {outrosPedidos.map((pedido) => (
                <button
                  key={pedido.id}
                  type="button"
                  onClick={() => onTrocarPedido(pedido.id)}
                  className="flex items-center gap-2 rounded-xl border border-solid border-neutral-200 bg-white px-3 py-2 text-left text-sm text-neutral-800 transition hover:border-primary-300 hover:bg-primary-50">
                  <FiPackage className="shrink-0 text-neutral-500" size={14} />
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold">Pedido #{pedido.id}</span>
                    {pedido.items_summary?.first_product_name && (
                      <span className="block truncate text-xs text-neutral-500">
                        {pedido.items_summary.first_product_name}
                      </span>
                    )}
                  </span>
                  <FiChevronRight className="shrink-0 text-neutral-400" size={14} />
                </button>
              ))}
            </div>
          </div>
        )}

        <MessageComposer
          disabled={!canWrite}
          isSending={isSending}
          placeholder={isClosed ? 'Atendimento encerrado' : 'Escreva sua mensagem…'}
          onSend={handleSend}
        />

        {/*
          Encerrar é diferente de fechar o painel: o X só esconde a janela e a
          conversa continua viva. Encerrar arquiva o atendimento e libera o
          atendente — por isso pede confirmação, numa folha que cobre o
          painel, com a explicação da diferença.
        */}
        <AnimatePresence>
          {isConfirmingClose && canClose && (
            <motion.div
              key="confirmar-encerramento"
              className="absolute inset-0 z-20 flex flex-col justify-end bg-neutral-900/30 backdrop-blur-[1px] sm:rounded-l-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsConfirmingClose(false)}>
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-label="Encerrar atendimento"
                className="flex flex-col gap-4 rounded-t-3xl bg-white px-5 pb-6 pt-3 shadow-2xl"
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 320 }}
                onClick={(event) => event.stopPropagation()}>
                <span aria-hidden className="mx-auto h-1 w-10 rounded-full bg-neutral-200" />

                <div className="flex flex-col items-center gap-2 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                    <FiLogOut size={20} />
                  </span>
                  <Typography.Text type="paragraphOne" weight="semibold">
                    Encerrar este atendimento?
                  </Typography.Text>
                  <Typography.Text type="captionOne" muted>
                    A conversa será arquivada. Se quiser só sair por agora, feche o painel no X — o atendimento continua
                    e você pode voltar depois.
                  </Typography.Text>
                </div>

                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    disabled={closeConversation.isPending}
                    onClick={() => uuid && closeConversation.mutate(uuid)}
                    className="flex h-11 items-center justify-center gap-2 rounded-xl border-0 bg-red-600 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60">
                    {closeConversation.isPending ? 'Encerrando…' : 'Encerrar atendimento'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsConfirmingClose(false)}
                    className="flex h-11 items-center justify-center rounded-xl border-0 bg-neutral-100 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-200">
                    Continuar conversando
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Sheet.Content>
    </Sheet.Root>
  )
}

export default OrderHelpPanel
